import { apiClient } from '@/api/api'
import type {
  TasteMatchingSwipeScore,
  TasteMatchingCard,
  TasteMatchingCardsResponse,
  TasteMatchingSwipeResponse,
  TasteMatchingBatchSwipeItem,
  TasteMatchingBatchSwipeResponse,
  TasteMatchingSessionStartRequest,
  TasteMatchingSessionStartResponse,
  SessionCardsResponse,
  SwipeOptimizedRequest,
  SwipeOptimizedResponse
} from '../types/taste-config'

export interface FetchTasteMatchingCardsParams {
  gender?: 'male' | 'female' | 'unisex'
  limit?: number // 1-50, default: 10
}

/**
 * @deprecated  session-based API: startTasteMatchingSession  fetchSessionCards
 *        Taste Matching API
 * 
 * @param params -  
 * @returns     
 */
export async function fetchTasteMatchingCards(
  params: FetchTasteMatchingCardsParams
): Promise<TasteMatchingCardsResponse> {
  try {
    const queryParams = new URLSearchParams()

    if (params.gender) {
      queryParams.append('gender', params.gender)
    }

    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }

    const url = `/taste-matching/cards/next${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await apiClient.get<TasteMatchingCardsResponse>(url)
    return response.data
  } catch (error) {
    console.error('Error fetching taste matching cards:', error)
    throw error
  }
}

export interface SubmitTasteMatchingSwipeParams {
  cardId: number
  action: 'like' | 'dislike' | 'skip'
  swipeTimeMs?: number
  sessionId?: string
  /** § 3.2 — the photo's rating, 1..5, agreeing with `action`; sent only when given. */
  score?: TasteMatchingSwipeScore
}

/**
 * @deprecated  submitSwipeOptimized  session-based API
 *     Taste Matching API
 * 
 * @param params -  
 * @returns   
 */
export async function submitTasteMatchingSwipe(
  params: SubmitTasteMatchingSwipeParams
): Promise<TasteMatchingSwipeResponse> {
  try {
    const { cardId, action, swipeTimeMs, sessionId, score } = params

    const body: {
      action: string
      swipeTimeMs?: number
      sessionId?: string
      score?: TasteMatchingSwipeScore
    } = {
      action
    }

    if (swipeTimeMs !== undefined) {
      body.swipeTimeMs = swipeTimeMs
    }

    if (sessionId) {
      body.sessionId = sessionId
    }

    if (score !== undefined) {
      body.score = score
    }

    const response = await apiClient.post<TasteMatchingSwipeResponse>(
      `/taste-matching/cards/${cardId}/swipe`,
      body
    )
    return response.data
  } catch (error) {
    console.error('Error submitting taste matching swipe:', error)
    throw error
  }
}

export interface SubmitTasteMatchingSwipeBatchParams {
  swipes: TasteMatchingBatchSwipeItem[]
  sessionId: string
}

/**
 *    (  )
 * 
 * @param params -   
 * @returns  
 */
export async function submitTasteMatchingSwipeBatch(
  params: SubmitTasteMatchingSwipeBatchParams
): Promise<TasteMatchingBatchSwipeResponse> {
  try {
    const { swipes, sessionId } = params

    const body = {
      swipes,
      sessionId
    }

    const response = await apiClient.post<TasteMatchingBatchSwipeResponse>(
      '/taste-matching/cards/swipe/batch',
      body
    )
    return response.data
  } catch (error) {
    console.error('Error submitting taste matching swipe batch:', error)
    throw error
  }
}

/**
 * Запуск сессии свайпов для Taste Matching API
 * 
 * @param styleIds - Массив integer ID стилей для оценки
 * @param mode - Режим показа карточек: 'sequential' (по стилям) или 'mixed' (перемешанные)
 * @param gender - Пол пользователя: 'male' или 'female' (опционально)
 * @returns Информация о созданной сессии
 */
export async function startTasteMatchingSession(
  styleIds: number[],
  mode: 'sequential' | 'mixed' = 'sequential',
  gender?: 'male' | 'female'
): Promise<TasteMatchingSessionStartResponse> {
  try {
    const body: TasteMatchingSessionStartRequest = {
      style_ids: styleIds,
      mode,
      ...(gender && { gender })
    }

    const response = await apiClient.post<TasteMatchingSessionStartResponse>(
      '/taste-matching/session/start',
      body
    )
    return response.data
  } catch (error) {
    console.error('Error starting taste matching session:', error)
    throw error
  }
}

/**
 *     
 * 
 * @param sessionId - ID ,   startTasteMatchingSession
 * @returns    
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export async function fetchSessionCards(
  sessionId: string
): Promise<SessionCardsResponse> {
  if (!sessionId || !UUID_REGEX.test(sessionId)) {
    throw new Error(`Invalid session ID: "${sessionId}"`)
  }

  try {
    const response = await apiClient.get<SessionCardsResponse>(
      `/taste-matching/session/${sessionId}/cards`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching session cards:', error)
    throw error
  }
}

/**
 *    -     
 * 
 * @param cardId - ID   
 * @param sessionId - ID  (!)
 * @param action - : 'like', 'dislike'  'skip'
 * @param swipeTimeMs -         ()
 * @returns         
 */
export async function submitSwipeOptimized(
  cardId: number,
  sessionId: string,
  action: 'like' | 'dislike' | 'skip',
  swipeTimeMs?: number,
  /** § 3.2 — the photo's rating, 1..5, agreeing with `action`; sent only when given. */
  score?: TasteMatchingSwipeScore
): Promise<SwipeOptimizedResponse> {
  try {
    const body: SwipeOptimizedRequest = {
      session_id: sessionId,
      action,
      ...(swipeTimeMs !== undefined && { swipe_time_ms: swipeTimeMs }),
      ...(score !== undefined && { score })
    }

    const response = await apiClient.post<SwipeOptimizedResponse>(
      `/taste-matching/cards/${cardId}/swipe-optimized`,
      body
    )
    return response.data
  } catch (error) {
    console.error('Error submitting optimized swipe:', error)
    throw error
  }
}


